import { React, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  List,
  CssBaseline,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Paper,
  Stack,
  Tooltip,
  Link,
  Alert,
  Snackbar,
  Typography,
} from "@mui/material";

import CircularProgress from "@mui/material/CircularProgress";

import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  getAppointmentInProgress,
  isPatientANJO,
  isTodayMultidisciplinaryConsultation,
  verificaPermissaoDeAcesso,
} from "../src/services/permissoesDeAcesso";

import { ReactComponent as IconSecurity } from "./images/security.svg";

import revELA from "./images/imagemRevELA.svg";
import logoRevELA from "./images/logoRevELA.svg";
import {
  AttachFile,
  BadgeOutlined,
  GridView,
  MenuBookRounded,
  ExpandMoreOutlined,
  ExpandLessOutlined,
  DescriptionOutlined,
  LightbulbOutlined,
  LiveHelpOutlined,
  ManageAccountsOutlined,
  CalendarMonthOutlined,
  EventNoteOutlined,
  PeopleAltOutlined,
  BarChartOutlined,
  HouseOutlined,
  ChevronLeft,
  Vaccines,
  FactCheckOutlined,
  Menu,
  MonitorHeartOutlined,
} from "@mui/icons-material";

import MuiBreadcrumbs from "./components/MuiBreadcrumbs";
import HeaderPerfil from "./components/HeaderPerfil";
import { permissionRoutes } from "./utils/permissionRoutes";
import { getItemDecrypt } from "./utils/cryptoData";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: theme.spacing(0, 1),
  background: "#EFF5F7",

  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

export function MenuOptions({
  text,
  icon,
  open,
  link,
  isActive = false,
  disabled = false,
  ...props
}) {
  const navigate = useNavigate();
  return (
    <Link
      component="button"
      sx={{ textDecoration: "none", width: "100%" }}
      onClick={() => (disabled ? {} : link ? navigate(link) : {})}
    >
      <ListItem
        key={`${text}`}
        disablePadding
        sx={
          !open
            ? {
                maxHeight: "32px",
                width: "56px",
                m: 0,
                padding: 0,
                display: "flex",
                alignItems: "center",
                background: isActive
                  ? "#D3E3EB !important"
                  : "#EFF5F7 !important",
                borderRight: isActive ? "2px solid #3E94C2 !important" : "none",
                fontSize: "2px",
                "& div:first-of-type": {
                  padding: "4px 9px !important",
                },
                transition: (theme) =>
                  theme.transitions.create(["margin", "width"], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
              }
            : {
                maxHeight: "32px",
                m: 0,
                padding: 0,
                background: "#EFF5F7 !important",
                "& div:first-of-type": {
                  padding: "2px 8px !important",
                },
                transition: (theme) =>
                  theme.transitions.create(["margin", "width"], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
              }
        }
      >
        <Tooltip
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "#D3E3EB",
              },
            },
          }}
          title={
            <Box
              sx={{
                fontSize: "16px",
                color: "#1E729F",
                background: "transparent !important",
                opacity: disabled ? 0.38 : 1,
              }}
            >
              {text}
            </Box>
          }
          placement="right"
          disableHoverListener={open}
        >
          <ListItemButton
            sx={
              open
                ? {
                    transition: (theme) =>
                      theme.transitions.create(["margin", "width"], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                      }),
                  }
                : {
                    maxHeight: "32px",
                    transition: (theme) =>
                      theme.transitions.create(["margin", "width"], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                      }),
                  }
            }
          >
            <ListItemIcon
              sx={{
                color: "#1E729F",
                minWidth: "30px",
                opacity: disabled ? 0.38 : 1,
              }}
            >
              {icon}
            </ListItemIcon>
            <ListItemText
              primary={`${text}`}
              primaryTypographyProps={{ fontSize: "0.8rem" }}
              sx={{ opacity: open ? 1 : 0 }}
            />
          </ListItemButton>
        </Tooltip>
      </ListItem>
    </Link>
  );
}

export function GroupList(props) {
  const { text, listItemGroup, open, openOption, handle, hide } = props;
  return (
    <List
      sx={{
        color: "#1E729F",
        padding: "0 0",
        display: hide ? "none" : "initial",
        fontSize: "0.1rem",
      }}
    >
      {!open && (
        <Divider
          sx={{
            width: "80%",
            margin: "16px auto",
            borderColor: "#063F5E",
          }}
        />
      )}
      {open && (
        <ListItem
          sx={{
            "&:hover": { cursor: "pointer" },
            margin: "6px 0px !important",
          }}
          onClick={handle}
        >
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            sx={{
              color: "#063F5E",
              padding: "0px 16px !important",
              width: "100%",
            }}
          >
            <ListItemText primary={text} />
            {openOption ? <ExpandMoreOutlined /> : <ExpandLessOutlined />}
          </Stack>
        </ListItem>
      )}
      {openOption | !open ? <Box>{listItemGroup}</Box> : <></>}
    </List>
  );
}

export function RootLayout({ children, ContentMaxWidth = "1250px" }) {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openGeral, setOpenGeral] = useState(true);
  const [openAreaPaciente, setOpenAreaPaciente] = useState(true);
  const [openConsulta, setOpenConsulta] = useState(true);
  const [loadingPermission, setLoadingPermission] = useState(true);
  const [errorPermission, setErrorPermission] = useState(false);
  const [pathRedirect, setPathRedirect] = useState("/home");

  const [isANJO, setIsANJO] = useState(null);
  const [isMultidisciplinary, setIsMultidisciplinary] = useState(null);
  const [ischeck, setIscheck] = useState(null);
  const [isAppointmentInProgress, setIsAppointmentInProgress] = useState(false);

  // let vinculo = JSON.parse(localStorage.getItem("vinculo_ativo"));
  let vinculo = getItemDecrypt("vinculo_ativo");
  let paciente = getItemDecrypt("paciente");

  useEffect(() => {
    async function fetchData() {
      const isANJO = await checkIsPatientANJO();
      const isMultidisciplinary = await checkIsMultidisciplinary();
      const ischeck = await checarAvaliacao();
      const isAppointmentInProgresss = await checkIsAppointmentInProgress();

      setIsANJO(isANJO);
      setIsMultidisciplinary(isMultidisciplinary);
      setIscheck(ischeck);
      setIsAppointmentInProgress(isAppointmentInProgresss);
    }
    fetchData();
  }, [id]);

  useEffect(() => {
    setLoadingPermission(true);
    permissionRoutes(location.pathname).then((values) => {
      if (!values.hasPermission) {
        setErrorPermission(true);
        setPathRedirect(values.path);
        // setTimeout(() => {
        //   window.location.href = values.path;
        // }, 5000);
      } else {
        setLoadingPermission(false);
      }
    });
  }, [location.pathname]);

  async function checarAvaliacao() {
    let permiteAcesso = await verificaPermissaoDeAcesso(id, vinculo.id);
    return permiteAcesso;
  }

  async function checkIsPatientANJO() {
    if (!!id) {
      const response = await isPatientANJO(id);

      return response;
    }
    return false;
  }

  async function checkIsMultidisciplinary() {
    if (!!id) {
      const response = await isTodayMultidisciplinaryConsultation(id);
      return response;
    } else {
      return false;
    }
  }
  async function checkIsAppointmentInProgress() {
    if (!!id) {
      const response = await getAppointmentInProgress(id, vinculo.id);
      return response;
    } else {
      return false;
    }
  }

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleOpenGeral = () => {
    setOpenGeral(!openGeral);
  };

  const handleOpenAreaPaciente = () => {
    setOpenAreaPaciente(!openAreaPaciente);
  };

  const handleConsulta = () => {
    setOpenConsulta(!openConsulta);
  };

  const atualizarPai = () => {
    // Atualiza o estado do pai quando o filho notificar a atualização
    window.location.reload();
  };

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <CssBaseline />
      <Paper
        sx={{
          position: "fixed",
          height: "calc(100vh - 80px)",
          minWidth: open ? "250px" : "56px",
          transition: "all 0.1s ease-in-out !important",
          zIndex: 1000,
          background: "#EFF5F7",
          "& div": {
            padding: "0px ",
            margin: "0px ",
          },
          "& li": {
            padding: "0px ",
            margin: "0px ",
          },
          boxSizing: "border-box !important",
        }}
      >
        <Collapse
          in={open}
          orientation="horizontal"
          collapsedSize="56px"
          sx={{
            boxSizing: "border-box !important",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: open ? "250px" : "56px",
              background: "#EFF5F7 !important",
              boxSizing: "border-box !important",
              height: "100%",
            }}
          >
            {open ? (
              <DrawerHeader>
                <Box
                  onClick={handleDrawerClose}
                  sx={{
                    "&:hover": { cursor: "pointer" },
                  }}
                >
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    sx={{
                      color: "#1E729F",
                      background: "#EFF5F7",
                      padding: "0px 0px",
                    }}
                  >
                    <ChevronLeft />
                    <Box
                      sx={{
                        fontSize: "1rem",
                        background: "inherit !important",
                      }}
                    >
                      Minimizar menu
                    </Box>
                  </Stack>
                </Box>
              </DrawerHeader>
            ) : (
              <DrawerHeader sx={{}}>
                <Box
                  onClick={handleDrawerOpen}
                  sx={{
                    "&:hover": { cursor: "pointer" },
                    paddingLeft: "12px !important",
                    background: "transparent !important",
                  }}
                >
                  <Stack
                    direction="column"
                    alignItems={"center"}
                    sx={{
                      color: "#1E729F",
                      background: "#EFF5F7",
                      width: "2rem",
                    }}
                  >
                    <Menu sx={{ width: "1.2rem" }} />
                    <Box
                      sx={{
                        fontSize: "10px",
                        background: "transparent !important",
                      }}
                    >
                      Menu
                    </Box>
                  </Stack>
                </Box>
              </DrawerHeader>
            )}

            <Divider
              color="#FFFFFF"
              sx={{
                width: "auto",
                margin: "0px 8px",
                background: "transparent !important",
              }}
            />

            <Stack
              direction={"column"}
              spacing={1}
              justifyContent="space-between"
              alignContent={"center"}
              sx={{
                height: "100%",
                overflow: "auto",
              }}
            >
              <Box bgcolor="transparent !important" height="100%">
                <GroupList
                  text={"Geral"}
                  open={open}
                  openOption={openGeral}
                  handle={handleOpenGeral}
                  listItemGroup={
                    <>
                      {vinculo && (
                        <>
                          <MenuOptions
                            text={"Home"}
                            icon={<HouseOutlined sx={{ width: "1.2rem" }} />}
                            open={open}
                            link="/home"
                            isActive={location.pathname === "/home"}
                          />

                          <MenuOptions
                            text={"Estatísticas"}
                            icon={<BarChartOutlined sx={{ width: "1.2rem" }} />}
                            open={open}
                            isActive={false}
                          />
                          <MenuOptions
                            text={"Pacientes"}
                            icon={
                              <PeopleAltOutlined sx={{ width: "1.2rem" }} />
                            }
                            open={open}
                            link="/pacientes"
                            isActive={location.pathname === "/pacientes"}
                          />
                          <MenuOptions
                            text={"Consultas"}
                            icon={
                              <EventNoteOutlined sx={{ width: "1.2rem" }} />
                            }
                            open={open}
                          />
                          <MenuOptions
                            text={"Agendamentos"}
                            icon={
                              <CalendarMonthOutlined sx={{ width: "1.2rem" }} />
                            }
                            open={open}
                            link="/agendamentos"
                            isActive={location.pathname === "/agendamentos"}
                          />
                          <MenuOptions
                            text={"Gerenciamento de usuários"}
                            icon={
                              <ManageAccountsOutlined
                                sx={{ width: "1.2rem" }}
                              />
                            }
                            disabled={vinculo?.administrador !== true ?? true}
                            open={open}
                            link="/gerenciamento-sistema"
                            isActive={
                              location.pathname === "/gerenciamento-sistema"
                            }
                          />
                        </>
                      )}
                      {paciente && (
                        <>
                          <MenuOptions
                            text={"Home"}
                            icon={<HouseOutlined sx={{ width: "1.2rem" }} />}
                            open={open}
                            link="/paciente/home"
                            isActive={location.pathname === "/paciente/home"}
                          />
                          <MenuOptions
                            text={"Gerenciamento de acessos"}
                            icon={
                              <EventNoteOutlined sx={{ width: "1.2rem" }} />
                            }
                            open={open}
                            link="/paciente/geracesso"
                            isActive={
                              location.pathname === "/paciente/geracesso"
                            }
                          />
                          <MenuOptions
                            text={"Escalas"}
                            icon={<GridView sx={{ width: "1.2rem" }} />}
                            open={open}
                            link="/paciente/escalaspaciente"
                            isActive={
                              location.pathname === "/paciente/escalaspaciente"
                            }
                          />
                        </>
                      )}
                    </>
                  }
                />

                {vinculo && (
                  <>
                    <GroupList
                      text={"Área do Paciente"}
                      open={open}
                      openOption={openAreaPaciente}
                      hide={!ischeck || id === undefined}
                      handle={handleOpenAreaPaciente}
                      listItemGroup={
                        <>
                          <MenuOptions
                            text={"Prontuário do paciente"}
                            icon={
                              <BadgeOutlined
                                sx={{ width: "20px", maxWidth: "20px" }}
                              />
                            }
                            open={open}
                            link={`/pacientes/prontuario/${id}`}
                            isActive={
                              location.pathname ===
                                `/pacientes/prontuario/${id}` ||
                              location.pathname ===
                                `/pacientes/detalhes-cadastro/${id}`
                            }
                          />
                          <MenuOptions
                            text={"Escalas"}
                            icon={<GridView sx={{ width: "1.2rem" }} />}
                            open={open}
                            link={`/escalas/${id}`}
                            isActive={location.pathname === `/escalas/${id}`}
                          />
                          <MenuOptions
                            text={"Monitoramento ANJO"}
                            icon={
                              <MonitorHeartOutlined sx={{ width: "1.2rem" }} />
                            }
                            open={open}
                            link={`/paciente/monitoramento/anjo/${id}`}
                            isActive={
                              location.pathname ===
                              `/paciente/monitoramento/anjo/${id}`
                            }
                            disabled={!isANJO}
                          />
                          <MenuOptions
                            text={"Recordatório alimentar"}
                            icon={<MenuBookRounded sx={{ width: "1.2rem" }} />}
                            open={open}
                            disabled
                          />
                          <MenuOptions
                            text={"Documentos e anexos"}
                            icon={<AttachFile sx={{ width: "1.2rem" }} />}
                            open={open}
                            disabled
                          />
                          <MenuOptions
                            text={"Acessos"}
                            icon={<IconSecurity style={{ width: "1.2rem" }} />}
                            open={open}
                            link="/pacientes/dados-paciente/1"
                          />
                        </>
                      }
                    />

                    <GroupList
                      text={"Consulta"}
                      open={open}
                      openOption={openConsulta}
                      hide={!ischeck || id === undefined}
                      handle={handleConsulta}
                      listItemGroup={
                        <>
                          {isAppointmentInProgress && (
                            <MenuOptions
                              text={"Consulta em andamento"}
                              icon={<Vaccines sx={{ width: "1.2rem" }} />}
                              open={open}
                              link={`/pacientes/primeira-consulta/${id}/?multidisciplinar=false`}
                              isActive={
                                location.pathname.includes(
                                  "primeira-consulta"
                                ) ||
                                location.pathname.includes("consulta-rotina")
                              }
                            />
                          )}

                          {isMultidisciplinary && (
                            <MenuOptions
                              text={"Progresso multidisciplinar"}
                              icon={
                                <FactCheckOutlined sx={{ width: "1.2rem" }} />
                              }
                              open={open}
                              link={`/pacientes/progresso-multidisciplinar/${id}`}
                              isActive={
                                location.pathname ===
                                `/pacientes/progresso-multidisciplinar/${id}`
                              }
                            />
                          )}
                        </>
                      }
                    />
                  </>
                )}
              </Box>
              <Stack bgcolor="transparent !important">
                {open ? (
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{ pl: "8px" }}
                    color="#063F5E"
                  >
                    Informações
                  </Typography>
                ) : (
                  <Divider
                    sx={{
                      width: "80%",
                      margin: "0 auto",
                      borderColor: "#063F5E",
                    }}
                  />
                )}
                <List sx={{ color: "#1E729F" }}>
                  <MenuOptions
                    text={"Ajuda"}
                    icon={<LiveHelpOutlined sx={{ width: "1.2rem" }} />}
                    open={open}
                  />
                  <MenuOptions
                    text={"Sugestões"}
                    icon={<LightbulbOutlined sx={{ width: "1.2rem" }} />}
                    open={open}
                  />
                  <MenuOptions
                    text={"Termos e condições"}
                    icon={<DescriptionOutlined sx={{ width: "1.2rem" }} />}
                    open={open}
                  />
                </List>
                <Stack height="100px">
                  {open ? (
                    <Stack height={"50px"}>
                      <img
                        src={revELA}
                        alt={"Imagem RevELA"}
                        width="100px"
                        style={{
                          marginLeft: "16px",
                        }}
                      />
                    </Stack>
                  ) : (
                    <Stack>
                      <img
                        src={logoRevELA}
                        alt={"Logo RevELA"}
                        width="30px"
                        style={{
                          marginLeft: "14px",
                        }}
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Collapse>
      </Paper>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          marginLeft: open ? "250px" : "56px",
          transition: "all 0.1s ease-in-out !important",
        }}
      >
        {vinculo && (
          <>
            <Box sx={{ minHeight: "80px" }}></Box>
            <Box
              sx={{
                position: "fixed",
                zIndex: 900,
                width: open ? "calc(100vw - 250px)" : "calc(100vw - 56px)",
              }}
            >
              <HeaderPerfil onAtualizacao={atualizarPai} />
            </Box>
          </>
        )}
        {paciente && <Box sx={{ minHeight: "20px" }}></Box>}

        <Box
          component="main"
          sx={{
            boxSizing: "border-box !important",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "0px 24px",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: ContentMaxWidth,
              boxSizing: "border-box !important",
              pb: 8,
            }}
          >
            <MuiBreadcrumbs />

            {loadingPermission ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>{children}</>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={errorPermission}
        onClose={() => {
          setErrorPermission(false);
          navigate(pathRedirect);
        }}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message="Você não tem permissão para acessar esta página. Caso acredite que isso seja um erro, entre em contato com o administrador do sistema."
      >
        <Alert
          severity="error"
          onClose={() => {
            setErrorPermission(false);
            navigate(pathRedirect);
          }}
        >
          "Você não tem permissão para acessar esta página. Caso acredite que
          isso seja um erro, entre em contato com o administrador do sistema."
        </Alert>
      </Snackbar>
    </Box>
  );
}
